// Polling and failure messages for an AI detection job. No renderer and no timers outside
// `pollUntilSettled`, so the schedule and the wording can be tested on their own.
import {problemMessage} from './client';

/** Give up on the wait after this long (the backend's worst case is about 63 s). */
export const POLL_BUDGET_MS = 90_000;

const FAST_POLLS = 10; // the first ten polls, one per second
const FAST_MS = 1000;
const BACKOFF_MS = [2000, 3000, 5000]; // then 2 s, 3 s, and 5 s from then on (the cap)

/** Delay before poll number `index` (0-based). */
export const pollDelayMs = (index) =>
  index < FAST_POLLS ? FAST_MS : BACKOFF_MS[Math.min(index - FAST_POLLS, BACKOFF_MS.length - 1)];

const abortError = () => Object.assign(new Error('Polling aborted'), {name: 'AbortError'});

const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(abortError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, {once: true});
  });

const isTerminal = (job) => job && (job.status === 'succeeded' || job.status === 'failed');

/**
 * Polls `fetchStatus(signal)` on the schedule above until the job is `succeeded` or `failed`, and
 * resolves with that job. Resolves `{status: 'timed-out'}` once `budgetMs` has passed without a
 * terminal state. Rejects with an AbortError when `signal` aborts, and with the fetch error when
 * the job is unknown or expired (HTTP 404). Other fetch errors are transient: polling goes on.
 */
export async function pollUntilSettled({fetchStatus, signal, budgetMs = POLL_BUDGET_MS}) {
  let elapsed = 0;
  for (let index = 0; ; index += 1) {
    const delay = Math.min(pollDelayMs(index), budgetMs - elapsed);
    await sleep(delay, signal);
    elapsed += delay;
    let job = null;
    try {
      job = await fetchStatus(signal);
    } catch (err) {
      if (signal.aborted || (err && err.response && err.response.status === 404)) throw err;
    }
    if (isTerminal(job)) return job;
    if (elapsed >= budgetMs) return {status: 'timed-out'};
  }
}

/** The server's problem slug: `upstream-timeout` from `https://.../problems/upstream-timeout`. */
export const problemSlug = (problem) =>
  String((problem && problem.type) || '')
    .split('/')
    .pop();

export const BUSY_MESSAGE = 'The analysis service is busy, try again in a minute.';
export const TIMEOUT_MESSAGE = 'The analysis took too long.';
export const BAD_RESULT_MESSAGE = 'We could not read the analysis result. Please try again.';
export const GENERIC_MESSAGE = 'Analysis failed. Please try again.';
export const GONE_MESSAGE = 'This analysis is no longer available. Start a new one.';
export const CONNECTION_MESSAGE = 'Check your connection and try again.';
export const IMAGE_MESSAGE = 'Failed to load image for analysis.';
export const STILL_PROCESSING_MESSAGE = 'Still processing. Try again in a moment.';

/** Message for a job that finished as `failed`, by its problem type. */
export const jobFailureMessage = (problem) => {
  switch (problemSlug(problem)) {
    case 'upstream-circuit-open':
    case 'upstream-unavailable':
      return BUSY_MESSAGE;
    case 'upstream-timeout':
      return TIMEOUT_MESSAGE;
    case 'upstream-bad-response':
      return BAD_RESULT_MESSAGE;
    default:
      return GENERIC_MESSAGE;
  }
};

/** Retry-After of a rejected request, in whole seconds, or null. */
export const retryAfterSeconds = (err) => {
  const raw = err && err.response && err.response.headers && err.response.headers['retry-after'];
  const seconds = parseInt(raw, 10);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
};

export const rateLimitMessage = (seconds) =>
  seconds
    ? `Too many analyses right now. Try again in ${seconds} ${seconds === 1 ? 'second' : 'seconds'}.`
    : 'Too many analyses right now. Try again in a minute.';

/** Message for a request that was refused or failed before a job existed (POST, or a GET). */
export const requestFailureMessage = (err) => {
  const status = err && err.response && err.response.status;
  if (status === 429) return rateLimitMessage(retryAfterSeconds(err));
  if (status === 503) return BUSY_MESSAGE; // the workflow service is unreachable
  if (status === 504) return TIMEOUT_MESSAGE;
  if (err && err.problem) {
    // Other 4xx carry the server's own field-level detail (for example an image outside storage).
    if (status && status < 500) return problemMessage(err, GENERIC_MESSAGE);
    return jobFailureMessage(err.problem);
  }
  if (!status) return CONNECTION_MESSAGE;
  return GENERIC_MESSAGE;
};
